import PropTypes from 'prop-types'
import BtnPlus from 'src/assets/img/common/btn_plus.svg'
import BtnMinus from 'src/assets/img/realtalk/btn_minus.svg'
import Heart from 'src/assets/img/realtalk/heart.svg'
import { useEffect, useState } from 'react'
import { useAuthState } from 'src/providers/AuthProviders'
import clsx from 'clsx'
import ModalCommon from 'src/components/common/ModalCommon'
import ExpiryStatusImage from 'src/components/menu/ExpiryStatusImage'

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}

const ModalItemDetail = ({
  item,
  subItems,
  handleCloseModalDetail,
  handleSendMessageItem,
  handleClose,
  setDataItem,
  open
}) => {
  const { user } = useAuthState()

  // Map of record id -> { checked, count }
  const [selections, setSelections] = useState({})
  const [totalItemLove, setTotalItemLove] = useState(0)

  // Initialize: first record checked with count 1, rest unchecked
  useEffect(() => {
    if (subItems && subItems.length > 0) {
      const initial = {}
      subItems.forEach((record, index) => {
        initial[record.id] = { checked: index === 0, count: index === 0 ? 1 : 1 }
      })
      setSelections(initial)
    }
  }, [subItems])

  const handleCheckboxChange = (record) => {
    setSelections((prev) => ({
      ...prev,
      [record.id]: {
        ...prev[record.id],
        checked: !prev[record.id]?.checked,
        count: !prev[record.id]?.checked ? 1 : prev[record.id]?.count
      }
    }))
  }

  const handleCountPlus = (recordId) => {
    setSelections((prev) => ({
      ...prev,
      [recordId]: { ...prev[recordId], count: prev[recordId].count + 1 }
    }))
  }

  const handleCountMinus = (recordId) => {
    setSelections((prev) => ({
      ...prev,
      [recordId]: { ...prev[recordId], count: prev[recordId].count - 1 }
    }))
  }

  const handleAddAll = (record) => {
    setSelections((prev) => ({
      ...prev,
      [record.id]: { checked: true, count: record.amount }
    }))
  }

  // Total count across all checked records
  const totalCount = subItems
    ? subItems.reduce((sum, r) => {
      const sel = selections[r.id]
      return sum + (sel?.checked ? sel.count : 0)
    }, 0)
    : 0

  // Calculate total LOVE per checked record (each record may have different increase_love)
  useEffect(() => {
    if (!item || !subItems) return
    const times = user.dream_compass ? item.increase_love_dream_compass : 1
    const total = subItems.reduce((sum, r) => {
      const sel = selections[r.id]
      if (sel?.checked) {
        return sum + r.increase_love * sel.count * times
      }
      return sum
    }, 0)
    setTotalItemLove(total)
  }, [item, subItems, selections])

  const closeModalDetail = () => {
    handleCloseModalDetail()
    setTimeout(() => {
      setSelections({})
    }, 200)
  }

  // For single-record items, keep simple +/- behavior
  const singleRecord = subItems?.length === 1 ? subItems[0] : null
  const singleCount = singleRecord ? (selections[singleRecord.id]?.count || 1) : 0

  const modalButtons = [
    {
      label: 'キャンセル',
      onClick: () => {
        closeModalDetail()
      },
      type: 'close'
    },
    {
      label: '決定',
      onClick: () => {
        if (totalCount === 0) return
        const checkedItems = subItems
          ?.filter((r) => selections[r.id]?.checked)
          .map((r) => ({ user_item_id: r.id, amount: selections[r.id].count }))
        if (!checkedItems || checkedItems.length === 0) return
        const firstChecked = subItems.find((r) => selections[r.id]?.checked)
        setDataItem(firstChecked)
        handleCloseModalDetail()
        handleClose()
        handleSendMessageItem(totalCount, firstChecked.item_id, checkedItems, totalItemLove)
      }
    }
  ]

  return (
    <ModalCommon title="アイテム送信" open={open} onClose={closeModalDetail} buttons={modalButtons}>
      <div className="bg-[#FFFFFF] px-2 pt-6 pb-12 text-center relative overflow-auto h-[350px] hidden-scroll">
        <h1 className="mb-5 font-Inter text-[#484848] font-bold text-[15px]">
          アイテムを何個送りますか？
        </h1>
        <div>
          {/* Single record: original layout with image + global +/- */}
          {singleRecord && (
            <>
              <div className="flex gap-5 items-center justify-center">
                <button
                  onClick={() => handleCountMinus(singleRecord.id)}
                  disabled={singleCount < 2}
                >
                  <img
                    className={`w-[30px] ${singleCount < 2 ? 'filter grayscale' : ''}`}
                    src={BtnMinus}
                  />
                </button>
                <ExpiryStatusImage
                  wrapperClassName="top-14 right-20 z-10"
                  textClassName="text-[#a1a1a1] font-bold align-top"
                  imageClassName="h-10 w-10"
                  status={singleRecord.expired_type ?? item.expired_type}
                />
                <ItemThumbnail
                  image={item.image}
                  imgClassName="w-[120px] h-[120px] object-cover"
                  className=""
                  rarity
                />
                <div>
                  <button
                    disabled={singleRecord.amount === singleCount}
                    onClick={() => handleCountPlus(singleRecord.id)}
                  >
                    <img
                      className={`${singleRecord.amount === singleCount ? 'filter grayscale' : ''} w-[30px]`}
                      src={BtnPlus}
                    />
                  </button>
                </div>
              </div>
              <div className="text-center my-2 font-Inter text-[#6F9BFF] font-semibold">
                贈るアイテム数 x
                <span className="text-[#484848] text-[19px] ml-2">{singleCount}</span>
              </div>
              <button
                className="bg-[#6F9BFF] w-[136px] p-1 rounded-[7px] text-white font-bold text-[15px]"
                onClick={() => handleAddAll(singleRecord)}
              >
                全て追加
              </button>
              <p className="w-[136px] m-auto justify-center font-bold bg-[#F7F7F7] font-Inter p-[5px] rounded-[7px] text-[10px] text-center mt-2 text-[#6F9BFF]">
                所持アイテム数
                <span className="text-[#484848] ml-2">{singleRecord.amount}</span>
              </p>
              <div className="flex justify-center mt-8">
                <div className="flex justify-around w-4/5 p-3 bg-[#F7F7F7] rounded-[7px] text-[#FF86C8] font-bold text-[15px] font-Inter">
                  <div className="flex">
                    <img className="w-[30px]" src={Heart} />
                    <span className="ml-1">LOVE度上昇</span>
                  </div>
                  <span>+ {totalItemLove}</span>
                </div>
              </div>
              <div className="mt-2">
                <p className="text-[10px] text-[#6F9BFF] font-semibold font-Inter">
                  夢のコンパス有効期限内は更に{item.increase_love_dream_compass}倍LOVE度加算
                </p>
              </div>
            </>
          )}

          {/* Multiple records: image + checkbox list with per-record +/- */}
          {subItems && subItems.length > 1 && (
            <>
              <div className="mt-4 mx-auto w-[90%]">
                <ItemThumbnail
                  image={item.image}
                  imgClassName="w-[120px] h-[120px] object-cover"
                  className="mb-2"
                  rarity
                />
                <p className="text-[11px] text-[#6F9BFF] font-bold font-Inter mb-2">
                  送るアイテムを選択
                </p>
                <div className="flex justify-center mt-8">
                  <div className="flex justify-around w-4/5 p-3 bg-[#F7F7F7] rounded-[7px] text-[#FF86C8] font-bold text-[15px] font-Inter">
                    <div className="flex">
                      <img className="w-[30px]" src={Heart} />
                      <span className="ml-1">LOVE度上昇</span>
                    </div>
                    <span>+ {totalItemLove}</span>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-[10px] text-[#6F9BFF] font-semibold font-Inter">
                    夢のコンパス有効期限内は更に{item.increase_love_dream_compass}倍LOVE度加算
                  </p>
                </div>
                <div className="flex flex-col gap-2 mt-2">
                  {subItems.map((record) => {
                    const sel = selections[record.id] || { checked: false, count: 1 }
                    return (
                      <div
                        key={record.id}
                        className={clsx(
                          'flex items-center justify-around gap-2 p-2 rounded-[7px] border text-left',
                          sel.checked
                            ? 'border-[#6F9BFF] bg-[#F0F6FF]'
                            : 'border-[#E0E0E0] bg-[#F7F7F7]'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={sel.checked}
                          onChange={() => handleCheckboxChange(record)}
                        />
                        <div className="text-[11px] font-Inter flex">
                          <div className="relative w-24">
                            <div className="text-[#484848] font-semibold">
                              消費期限: {formatDate(record.expired_at)}
                            </div>
                            <div className="text-[#6F9BFF] font-bold">所持数: x{record.amount}</div>
                          </div>
                          <ExpiryStatusImage
                            wrapperClassName="relative"
                            textClassName="text-[#a1a1a1] font-bold align-top"
                            imageClassName="h-6 w-6"
                            status={record.expired_type}
                          />
                        </div>
                        <div
                          className={`flex items-center gap-1 ${sel.checked ? 'visible' : 'invisible'}`}
                        >
                          <button
                            onClick={() => handleCountMinus(record.id)}
                            disabled={sel.count < 2}
                          >
                            <img
                              className={`w-[18px] ${sel.count < 2 ? 'filter grayscale' : ''}`}
                              src={BtnMinus}
                            />
                          </button>
                          <span className="text-[#484848] font-bold text-[18px] min-w-[20px] text-center">
                            {sel.count}
                          </span>
                          <button
                            onClick={() => handleCountPlus(record.id)}
                            disabled={sel.count >= record.amount}
                          >
                            <img
                              className={`w-[18px] ${sel.count >= record.amount ? 'filter grayscale' : ''}`}
                              src={BtnPlus}
                            />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </ModalCommon>
  )
}

ModalItemDetail.propTypes = {
  item: PropTypes.object.isRequired,
  subItems: PropTypes.array.isRequired,
  handleCloseModalDetail: PropTypes.func.isRequired,
  handleSendMessageItem: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  setDataItem: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired
}

const ItemThumbnail = ({
  image,
  rarity,
  className = 'w-[72px] h-[80px] border-menu-dark-blue rounded-sm',
  imgClassName = 'w-[60px]'
}) => {
  return (
    <div
      className={clsx(
        'flex justify-center items-center relative',
        className,
        !rarity && 'border-2 drop-shadow-md bg-white'
      )}
    >
      <img className={clsx(imgClassName)} src={image} />
    </div>
  )
}

ItemThumbnail.propTypes = {
  image: PropTypes.string.isRequired,
  rarity: PropTypes.bool,
  className: PropTypes.string,
  imgClassName: PropTypes.string
}

export default ModalItemDetail
